import type { Claim } from '@/types/claim'
import type { ProductType } from '@/types/productTypes'
import { brokerIsStroobants } from '@/utils/brokers'
import { filterActiveClaims, filterClosedClaims, filterStroobantsClaims } from '@/utils/claims'
import type { RequestState } from '@/utils/restStore'
import { RequestStatus, handleRequest, initialRequestState } from '@/utils/restStore'
import { defineStore } from 'pinia'

import Api from '@/services/api'

interface State {
  claims: RequestState<Claim[]>
  selectedStatus: ClaimsTabs
  selectedContractNumber: string | null
}

export enum ClaimsTabs {
  Active = 'active',
  Closed = 'closed'
}

interface ClaimContract {
  contractType: ProductType
  contractNumber: string
}

export const claimTabs = [ClaimsTabs.Active, ClaimsTabs.Closed]

const initialState = (): State => ({
  claims: initialRequestState(),
  selectedStatus: ClaimsTabs.Active,
  selectedContractNumber: null
})

export const useClaimsStore = defineStore({
  id: 'claims',
  state: initialState,
  actions: {
    selectStatus(status: ClaimsTabs) {
      this.selectedStatus = status
    },
    selectContract(contractNumber: string | null) {
      this.selectedContractNumber = contractNumber
    },
    async fetchClaims() {
      await handleRequest(Api.getClaims, (claims: RequestState<Claim[]>) => {
        this.claims = claims
      })
      if (this.claims.status === RequestStatus.RequestLoaded && brokerIsStroobants()) {
        this.claims.data = filterStroobantsClaims(this.claims.data)
      }
    },
    async fetchCompanyClaims(companyId: string) {
      await handleRequest(
        () => Api.getCompanyClaims(companyId),
        (claims: RequestState<Claim[]>) => {
          this.claims = claims
        }
      )
      if (this.claims.status === RequestStatus.RequestLoaded && brokerIsStroobants()) {
        this.claims.data = filterStroobantsClaims(this.claims.data)
      }
    }
  },
  getters: {
    contractsWithClaims: (state: State): ClaimContract[] => {
      if (state.claims.status !== RequestStatus.RequestLoaded) {
        return []
      }
      const contracts = state.claims.data.reduce((contracts: ClaimContract[], claim) => {
        if (!contracts.some((contract) => contract.contractNumber === claim.contractNumber)) {
          contracts.push({ contractType: claim.productType, contractNumber: claim.contractNumber })
        }
        return contracts
      }, [])

      return contracts
    },
    activeClaims: (state: State): Claim[] => {
      if (state.claims.status !== RequestStatus.RequestLoaded) {
        return []
      }
      const activeClaims = filterActiveClaims(state.claims.data)
      if (state.selectedContractNumber) {
        return activeClaims.filter((claim) => claim.contractNumber === state.selectedContractNumber)
      }
      return activeClaims
    },
    closedClaims: (state: State): Claim[] => {
      if (state.claims.status !== RequestStatus.RequestLoaded) {
        return []
      }
      const closedClaims = filterClosedClaims(state.claims.data)
      if (state.selectedContractNumber) {
        return closedClaims.filter((claim) => claim.contractNumber === state.selectedContractNumber)
      }
      return closedClaims
    }
  }
})
